import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, ChangeDetectorRef, Inject, Optional } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { GridOptions } from 'ag-grid';
import { LogService, ISettingsService, SettingsDetails } from '@pershing/pershing-core';
import { PershingGridPreferenceUtil } from '../utils/pershing-grid-preference.util';
import { PershingExportEvent, PershingExportOption } from '../models/pershing-export-config.model';
import { GridColumn } from './columns/pershing-grid-columns.component';

const FILTER_TYPE_EXPORT_DISPLAY_MAP: any = {
    eq: '',
    ne: 'not equal',
    lt: 'less than',
    lte: 'less than or equal to',
    gt: 'greater than',
    gte: 'greater than or equal to',
    contains: 'contains',
    notContains: 'not contains'
};

@Component({
    selector: 'pershing-grid-controller',
    templateUrl: './pershing-grid-controller.component.html',
    providers: [ TranslatePipe ]
})
export class PershingGridControllerComponent implements OnInit, OnChanges, OnDestroy {

    static readonly COMPACT_ROW_HEIGHT = 26;
    static readonly COMFORTABLE_ROW_HEIGHT = 44;
    static readonly COMPACT_THEME = 'compact';

    @Input() gridOptions: GridOptions;
    @Input() totalRowCount: number = 0; // Used by Pagination and ScrollKey, otherwise this will be deprecated for FSP Pagination
    @Input() floatingFilterHeight = 36;
    @Input() pageSize: number;

    // Object Type emitted depends on Pagination implmentation (scrollKey enable vs disabled)
    @Output() onPageChanged: EventEmitter<PershingPaginationProperties | string> = new EventEmitter<PershingPaginationProperties | string>();

    // ScrollKey Control Settings
    @Input() enableScrollKey: boolean = false;
    canNext: boolean = false;
    canPrevious: boolean = false;
    nextScrollKey: string = null;
    scrollKeyList: Array<string> = [];

    // Preference settings Inputs - not mandatory for features which dont support user preferences
    @Input() preferenceSettings: SettingsDetails;
    @Input() preferenceSelectKey: string;
    @Input() defaultColumnSet: any;

    // Export related inputs
    @Input() exportOption: PershingExportOption;
    @Output() onExportClicked: EventEmitter<PershingExportEvent> = new EventEmitter<PershingExportEvent>();

    // Pagination fields
    currentPage: number = 1;
    pages: number[];
    startRow: number = 0;
    endRow: number = 0;
    lastPage: number;
    rowCount: number; // For client side filter with no pagination

    // Filter fields
    showFilters = false;

    // Column Menu fields
    @Input() maxPinned = 3;
    @Input() showColumns: boolean = false;

    resizeTimeout: any;

    gridInstantiated = false;
    parentGridReadyEvent: any;

    /**
     * Constructor for PershingGridControllerComponent.
     * @param preferencesService
     * @param logService
     */
    constructor(private preferencesService: ISettingsService, private logService: LogService, private cdRef: ChangeDetectorRef,
                private translatePipe: TranslatePipe, @Optional() @Inject('AppSettings') private appSettings: any) {}

    /**
     * ngOnInit() method invoked as part of Angular lifecycle after the constructor. This is where
     * input data binded fields become available
     */
    ngOnInit(): void {
        // Inititializing the columnTypes if it is not defined in grid options
        if (!this.gridOptions.columnTypes) {
            this.gridOptions.columnTypes = {};
        }
        // Default configuration for NEW_COLUMN in column types - this will be used by columns widget
        this.gridOptions.columnTypes.NEW_COLUMN = {};
        // As pershing grid controller completely depends on AgGrid's grid api and column api
        // Making the pershing-grid controller widget invisible till the grid is instantiated
        this.parentGridReadyEvent = this.gridOptions.onGridReady ;
        if (!this.gridOptions.columnApi) {
            this.gridOptions.onGridReady = this.pershingControllerGridReadyEvent.bind(this);
        } else {
            this.gridInstantiated = true;
        }

        if (this.enableScrollKey) {
            this.reset();
        } else {
            this.resetRowHeightBasedOnTheme();
            // If pageSize is not passed via inpurt property data binding AND pagination is enabled, then take paginationPageSize from gridOptions
            if (!this.pageSize && this.gridOptions.pagination) {
                this.pageSize = this.gridOptions.paginationPageSize;
            }
            // With gridOptions.pagination set to true, the grid will want to show its pagination panel. Suppress it.
            this.gridOptions.suppressPaginationPanel = true;

            // If this is client side filtering, then you need to ensure the
            // client side record count gets updated every time the rows get updated
            if (this.gridOptions.enableFilter) {
                this.gridOptions.onModelUpdated = (() => {
                    this.rowCount = this.gridOptions.api.paginationGetRowCount();
                    this.cdRef.detectChanges();
                }).bind(this);
            }

            if (this.showColumns) {
                this.gridOptions.suppressMovableColumns = true;

                this.gridOptions.onColumnResized = ((event) => {
                    if (event.columns && event.columns.length > 1) {
                        this.logService.log('No support for PERSISTANCE - on multiple column resize at the same time');
                    } else {
                        const changedColumn = event.columns[0];
                        if (changedColumn) {
                            this.logService.log(`PershingGridControllerComponent - column resize : column id : ${changedColumn.getColId()}
                                            : defined width - ${changedColumn.getColDef().width}
                                        : current width - ${changedColumn.getActualWidth()}`);
                            if (this.resizeTimeout) {
                                clearTimeout(this.resizeTimeout);
                            }
                            this.resizeTimeout = setTimeout(() => {
                                this.savePreferences();
                            }, 200);
                        }
                    }
                });
            }
        }
    }

    /**
     * ngOnChanges() method invoked as part of Angular lifecycle.
     * This will be invoked when your data binded fields change. In this case, the
     * totalRowCount field will be changed from null to a number.
     */
    ngOnChanges(): void {
        if (!this.totalRowCount) {
            this.totalRowCount = 0;
        }
        if (this.enableScrollKey) {
            if (this.totalRowCount === 0) {
                this.reset();
            }
        } else {
            // TODO: Remove this lifecycle hook once deprecation removed
            this.updatePageDropdown();
            this.resetRowHeightBasedOnTheme();
        }
    }

    /**
     * ngDestroy() method invoked as part of Angular lifecycle.
     * Use for any custom cleanup that needs to occur prior to when the
     * ScrollKey instance is destroyed.
     */
    ngOnDestroy(): void {
        if (this.enableScrollKey) {
            this.reset();
        }
        if (this.gridOptions) {
            this.gridOptions.onGridReady = this.parentGridReadyEvent;
        }
    }

    pershingControllerGridReadyEvent(event): void {
        if (this.parentGridReadyEvent) {
            this.parentGridReadyEvent(event);
        }
        this.gridInstantiated = true;
        this.cdRef.detectChanges();
    }
    /**
     * Toggles the floating filters that appear under the header of each column.
     * The grid component must define what floating filters to use
     */
    toggleFilter(): void {
        this.showFilters = !this.showFilters;
        this.gridOptions.floatingFilter = this.showFilters;
        if (this.showFilters) {
            this.gridOptions.api.setFloatingFiltersHeight(this.floatingFilterHeight);
        } else {
            this.gridOptions.api.setFloatingFiltersHeight(0);
            if (Object.keys(this.gridOptions.api.getFilterModel()).length !== 0) { // If filter was already applied
                this.gridOptions.api.setFilterModel({}); // Clears all filters
            }
        }
        this.gridOptions.api.refreshHeader();
    }

    nextPage(): void {
        if (this.enableScrollKey) {
            this.onPageChanged.emit(this.nextScrollKey);
            this.disableControls();
        } else {
            if (this.currentPage < this.lastPage) {
                this.onPageChanged.emit({ pageNumber: ++this.currentPage, pageSize: this.pageSize });
            }
        }
    }

    /**
     * Iterates twice to remove previous scrollKey (removes current and previous scrollKey).
     * Current scrollkey was inserted when updateScrollKey method was called.
     */
    previousPage(): void {
        if (this.enableScrollKey) {
            let countPages = 0;
            while (countPages < 2) {
                if (this.scrollKeyList.length > 0) {
                    this.scrollKeyList.pop();
                }
                countPages = countPages + 1;
            }
            this.nextScrollKey = this.scrollKeyList.length > 0 ? this.scrollKeyList[this.scrollKeyList.length - 1] : '';
            this.onPageChanged.emit(this.nextScrollKey);
            this.disableControls();
        } else {
            if (this.currentPage !== 1) {
                this.onPageChanged.emit({ pageNumber: --this.currentPage, pageSize: this.pageSize });
            }
        }
    }

    goToPage(): void {
        this.onPageChanged.emit({ pageNumber: this.currentPage, pageSize: this.pageSize });
    }

    /**
     * This method should be invoked by the parent grid component AFTER the latest records
     * are retrieved and drawn on the grid. Pass the number of records displayed on the grid.
     * The pagination labels will automatically update.
     * @param countOnPage
     */
    updatePaginationDisplay(displayedRecordCount: number, pageIndex: number, totalRecords?: number): void {
        if (totalRecords !== null && totalRecords !== undefined) { // TODO: remove totalRowCount for good. Pass totalRecords to updatePageDropdown(...);
            this.totalRowCount = totalRecords;
        }
        if (this.totalRowCount > 0) {
            this.currentPage = pageIndex;
            if (this.gridOptions.api) {
                this.startRow = (pageIndex - 1) * this.pageSize + 1;
                this.endRow = (pageIndex - 1) * this.pageSize + displayedRecordCount;
            }
        } else {
            this.startRow = 0;
            this.endRow = 0;
            this.totalRowCount = 0;
        }
        this.updatePageDropdown();
    }

    /**
     * With the page size and total number of records, you can determine
     * how many pages to have as options in the dropdown
     */
    private updatePageDropdown() { // TODO: Pass in totalRecords as parameter
        this.lastPage = Math.ceil(this.totalRowCount / this.pageSize);
        // If last page is 5, construct an array [ 1, 2, 3, 4, 5 ]
        this.pages = Array.from({ length: this.lastPage }, (value, index) => index + 1);
    }

    /**
     * Reset filter state in grid controller
     */
    reset(): void {
        if (this.enableScrollKey) {
            this.resetScrollSettings();
        } else {
            if (this.showFilters) {
                this.toggleFilter();
            }
        }
    }

    savePreferences(): void {
        if (this.preferencesService) {
            // get current column state from ag-grid
            let colState = this.gridOptions.columnApi.getColumnState();
            let colStateToSave = PershingGridPreferenceUtil.prepareColumnStateToSave(colState);
            let settings = null;

            if (this.preferenceSettings) {
                settings = PershingGridPreferenceUtil.packGridSettings(this.preferenceSettings, this.preferenceSelectKey, colStateToSave);
                this.logService.log(`savePreferences() : grid-view-settings [key -
                                        ${ (this.preferenceSelectKey && this.preferenceSelectKey.trim() !== '') ?
                                             this.preferenceSelectKey : 'default'}] ->`, colStateToSave);
                this.preferenceSettings.settings = settings;
                this.logService.log('savePreferences() : Grid Preference settings to save :', this.preferenceSettings);
                // save preferences - error handling to be done if needed
                this.preferencesService.saveUserSettings(this.preferenceSettings);
            } else {
                this.logService.error('savePreferences() : Could not save preferences as preferences settings input not provided by the feature');
            }
        }
    }

    applyColumnPreferences(gridColumnList: Array<GridColumn>) {
        this.logService.log(gridColumnList);
        gridColumnList.forEach((column, index) => {
            if (!column.hideInColumns && column.hideInColumns !== true && column.headerName !== '') {
                this.gridOptions.columnApi.setColumnVisible(column.colId, column.visibility);
                this.gridOptions.columnApi.setColumnPinned(column.colId, column.pinned ? 'left' : null);
            }
            this.gridOptions.columnApi.moveColumn(column.colId, index);
        });
        this.savePreferences();
    }

    updateExportAction(exportFormat: string, exportDataSet: string): void {
        let exportEvent  = new PershingExportEvent();
        exportEvent.exportColumns = this.getExportColumns();
        exportEvent.exportFilterOptions = this.getFilterOptions();
        exportEvent.exportFormat = exportFormat;
        exportEvent.exportDataSet = exportDataSet;
        this.onExportClicked.emit(exportEvent);
    }

    exportEnabled(): boolean {
        return this.exportOption != null &&
                (this.exportOption.pdfCurrentPage ||
                    this.exportOption.pdfAllPage ||
                    this.exportOption.excelAllPage ||
                    this.exportOption.excelCurrentPage);
    }

    getExportColumns(): any {
        let exportColumns = [];
        let columns = this.gridOptions.columnApi.getAllDisplayedColumns();
        for (let i = 0; i < columns.length; i++ ) {
            let columnDef = columns[i]['colDef'];
            if (!columnDef['hideInColumns']) {
                exportColumns.push(this.getExportColumn(this.translatePipe.transform(columnDef['headerName']),
                    columnDef['field'], columnDef['cellClass'], this.determineExportColumnWidth(columnDef, columns[i]),
                    columnDef['exportCellformatter'], columnDef['pinned'] !== undefined));
            }
        }
        return exportColumns;
    }

    getExportColumn(displayName, field, cssClass, columnWidth, formatter, fixed): any {
        let exportColumn = { };
        exportColumn['displayName'] = displayName;
        exportColumn['column'] = field;
        exportColumn['cssClass'] = cssClass;
        exportColumn['columnWidth'] = columnWidth;
        exportColumn['formatter'] = formatter;
        exportColumn['fixed'] = fixed;
        return exportColumn;
    }

    determineExportColumnWidth(columnDef: any, column: any): any {
        let returnWidth;
        let width = columnDef['width'];
        if (width === undefined) { // width is not defined in column config
            let actualWidth = column.actualWidth;
            if (actualWidth === undefined) { // Grid column actual width is not available
                let minWidth = columnDef['minWidth'];
                let maxWidth = columnDef['maxWidth'];
                if (maxWidth !== undefined && minWidth === undefined) { // only max width is defined
                    returnWidth = maxWidth;
                } else if (maxWidth === undefined && minWidth !== undefined) { // only min width is defined
                    returnWidth = minWidth;
                } else if (maxWidth !== undefined && minWidth !== undefined) { // both max and min width is defined
                    if (this.exportOption.useMinWidth !== undefined && this.exportOption.useMinWidth === true) {
                        returnWidth = minWidth;
                    } else {
                        returnWidth = maxWidth;
                    }
                }
            } else { // Grid column actual width is available
                returnWidth = actualWidth;
            }
        } else { // width is defined in column definition
            returnWidth = width;
        }
        return returnWidth;
    }

    getFilterOptions(): any {
        let filterOptions = [];
        let filterModel = this.gridOptions.api.getFilterModel();
        Object.keys(filterModel).forEach(key => {
            let filterOption = {};
            filterOption['value'] = this.transformFilterType(filterModel[key]['type']) + filterModel[key]['filter'];
            filterOption['type'] = filterModel[key]['type'];
            filterOption['displayName'] = this.transformFilterDisplayName(key);
            filterOptions.push(filterOption);
        });
        return filterOptions;
    }

    transformFilterDisplayName(filterKey): string {
        let filterDisplayName = filterKey;
        if (this.gridOptions.columnApi.getColumn(filterKey) &&
                this.gridOptions.columnApi.getColumn(filterKey).getColDef() &&
                this.gridOptions.columnApi.getColumn(filterKey).getColDef().headerName) {
            filterDisplayName = this.translatePipe.transform(this.gridOptions.columnApi.getColumn(filterKey).getColDef().headerName);
        }
        return filterDisplayName;
    }

    transformFilterType(type: string): string {
        return FILTER_TYPE_EXPORT_DISPLAY_MAP[type] !== '' ? (FILTER_TYPE_EXPORT_DISPLAY_MAP[type] + ' ') : '';
    }

    resetRowHeightBasedOnTheme() {
        let refreshNeeded = false;

        const gridFirstNode = (this.gridOptions.api && this.gridOptions.api.getFirstDisplayedRow() >= 0) ?
                                         this.gridOptions.api.getDisplayedRowAtIndex(this.gridOptions.api.getFirstDisplayedRow()) : null;


        if ((this.appSettings && this.appSettings.theme && this.appSettings.theme === PershingGridControllerComponent.COMPACT_THEME) ||
                (document.body.classList && document.body.classList.contains(PershingGridControllerComponent.COMPACT_THEME))) {
            // check if grid height (for the current theme) is already applied for the first row if it exists,
            //       if already applied, then no need to refresh again with the current theme height
            refreshNeeded = gridFirstNode ? gridFirstNode.rowHeight !== PershingGridControllerComponent.COMPACT_ROW_HEIGHT : false;

            this.gridOptions.rowHeight = PershingGridControllerComponent.COMPACT_ROW_HEIGHT;
            this.gridOptions.headerHeight = PershingGridControllerComponent.COMPACT_ROW_HEIGHT;
            if (this.gridOptions.getRowHeight) {
                this.gridOptions.getRowHeight = () => {
                    return PershingGridControllerComponent.COMPACT_ROW_HEIGHT;
                };
            }
        } else {
            // check if grid height (for the current theme) is already applied for the first row if it exists,
            //       if already applied, then no need to refresh again with the current theme height
            refreshNeeded = gridFirstNode ? gridFirstNode.rowHeight !== PershingGridControllerComponent.COMFORTABLE_ROW_HEIGHT : false;

            this.gridOptions.rowHeight = PershingGridControllerComponent.COMFORTABLE_ROW_HEIGHT;
            this.gridOptions.headerHeight = PershingGridControllerComponent.COMFORTABLE_ROW_HEIGHT;
            if (this.gridOptions.getRowHeight) {
                this.gridOptions.getRowHeight = () => {
                    return PershingGridControllerComponent.COMFORTABLE_ROW_HEIGHT;
                };
            }
        }

        if (this.gridOptions.api && refreshNeeded) {
            this.gridOptions.api.resetRowHeights();
            this.gridOptions.api.refreshHeader();
        }
    }

    nextButton(): boolean {
        if (this.totalRowCount === 0) {
            return true;
        } else if (this.enableScrollKey) {
            return !this.canNext;
        } else {
            return this.currentPage === this.lastPage;
        }
    }

    previousButton(): boolean {
        if (this.totalRowCount === 0) {
            return true;
        } else if (this.enableScrollKey) {
            return !this.canPrevious;
        } else {
            return this.currentPage === 1;
        }
    }

    private disableControls(): void {
        this.canPrevious = false;
        this.canNext = false;
    }

    /**
     * Method resets fields to default values.
    */
    resetScrollSettings(): void {
        this.canNext = false;
        this.canPrevious = false;
        this.nextScrollKey = '';
        this.scrollKeyList = [];
    }

    /**
     * Method receives the current scrollKey and adds to the array.
     * In addition, sets the next and previous fields to true or false;
    */
    public updateScrollKey (scrollKey: string): void {
        if (scrollKey != null && scrollKey.length > 0) {
            this.canNext = true;
            this.nextScrollKey = scrollKey;
            this.scrollKeyList.push(scrollKey);
            this.canPrevious = this.scrollKeyList.length > 1;
        } else if (scrollKey === null && this.scrollKeyList.length > 0) {
            this.canNext = false;
            this.scrollKeyList.push('LAST');
            this.canPrevious = this.scrollKeyList.length > 1;
        }
    }
}

export interface PershingPaginationProperties {
    pageNumber: number;
    pageSize: number;
}

@Component({
    selector: 'export-error-dialog',
    template: `
    <mat-dialog-content>
        <div class="row margin-0">
            <div class="col-xs-2 col-sm-1 messaging-icon"><i class="inline-icon fas fa-2x fa-exclamation-circle negative"></i></div>
            <div class="col-xs-10 col-sm"> There was an error while generating the report. Please try again.</div>
        </div>
    </mat-dialog-content>
    <div mat-dialog-actions>
        <div class="action-buttons">
            <button mat-raised-button mat-dialog-close="OK" color="primary" class="small-button">Close</button>
        </div>
    </div>`
  })
export class ExportErrorDialog {
    constructor (public dialogRef: MatDialogRef<ExportErrorDialog>, @Inject(MAT_DIALOG_DATA) public data: any) {}

    closeDialog(): void {
      this.dialogRef.close();
    }
}
